// Segundo a documentação, não é necessário utilizar o prefixo para Links e Imagens que usam o Next.js Image:
// https://nextjs.org/docs/app/api-reference/config/next-config-js/basePath
// Demais usos de URL de 'public' devem usar o prefixo para funcionar corretamente.
// export const buildImageSrc = (url: string): string => {
//   if (!url) return url;
//
//   let path = url;
//
//   const normalizePath = (base: string, path: string): string => {
//     if (!base) return path;
//     return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
//   };
//
//   if (process.env.NEXT_PUBLIC_BASE_PATH && !path.startsWith(process.env.NEXT_PUBLIC_BASE_PATH)) {
//     path = normalizePath(process.env.NEXT_PUBLIC_BASE_PATH, path);
//   }
//
//   if (process.env.NEXT_PUBLIC_ASSET_PREFIX && !path.startsWith(process.env.NEXT_PUBLIC_ASSET_PREFIX)) {
//     path = normalizePath(process.env.NEXT_PUBLIC_ASSET_PREFIX, path);
//   }
//
//   return path;
// };

/**
 * Constrói o caminho completo de uma imagem, aplicando BASE_PATH e ASSET_PREFIX
 * de forma inteligente para evitar duplicação e garantir a URL correta.
 *
 * @param url A URL original da imagem (pode ser relativa, já com basePath, ou já absoluta).
 * @returns A URL da imagem com os prefixos corretos aplicados.
 */
export const buildImageSrc = (url: string): string => {
  if (!url) {
    return url;
  }

  // basePath é sempre uma raiz. Se vier sem a barra ('cases' em vez de
  // '/cases'), normaliza aqui: senão o `startsWith` abaixo não reconhece uma URL
  // que já tem o basePath, e o caminho final sai relativo à página atual.
  const rawBasePath = process.env.NEXT_PUBLIC_BASE_PATH;
  const basePath = rawBasePath && !rawBasePath.startsWith('/') ? `/${rawBasePath}` : rawBasePath;
  const assetPrefix = process.env.NEXT_PUBLIC_ASSET_PREFIX;

  /**
   * Função auxiliar para combinar segmentos de caminho de forma segura,
   * garantindo uma única barra entre as partes e removendo barras extras.
   *
   * A barra inicial do primeiro segmento é preservada: sem ela, um basePath
   * '/cases' viraria 'cases' e o caminho deixaria de ser absoluto — numa página
   * servida em /cases/, 'cases/images/x.avif' resolve para
   * '/cases/cases/images/x.avif'. Um assetPrefix absoluto ('https://...') não
   * tem barra inicial e continua saindo intacto.
   *
   * @param segments Uma lista de strings para serem combinadas em um único caminho.
   * @returns O caminho combinado e normalizado.
   */
  const combinePaths = (...segments: string[]): string => {
    const parts = segments.filter(s => s);
    const rootSlash = parts[0] && parts[0].startsWith('/') ? '/' : '';

    return rootSlash + parts
      .map(s => s.replace(/^\/+|\/+$/g, ''))
      .filter(s => s)
      .join('/');
  };

  if (url.startsWith('http://') || url.startsWith('https://')) {
    if (assetPrefix && url.startsWith(assetPrefix)) {
      let remainingPath = url.substring(assetPrefix.length);
      if (remainingPath.startsWith('/')) {
        remainingPath = remainingPath.substring(1);
      }

      if (basePath) {
        if (remainingPath.startsWith(basePath.replace(/^\/|\/$/g, ''))) {
          return url;
        }
        return combinePaths(combinePaths(assetPrefix, basePath), remainingPath);
      }
      return url;
    }
    return url;
  }
  if (basePath && url.startsWith(basePath)) {
    if (assetPrefix) {
      return combinePaths(assetPrefix, url);
    }
    return url;
  }

  let finalRelativePath = url;
  if (finalRelativePath.startsWith('/')) {
    finalRelativePath = finalRelativePath.substring(1);
  }

  if (assetPrefix) {
    if (basePath) {
      return combinePaths(combinePaths(assetPrefix, basePath), finalRelativePath);
    }
    return combinePaths(assetPrefix, finalRelativePath);
  }

  if (basePath) {
    return combinePaths(basePath, finalRelativePath);
  }
  return url;
};
